import { treatErrorAs, handleErrorsInQueue, registerError } from 'api/errors';
import { errorDisplayTypes, errorLevel } from 'constants/errors';
import wam from '@tcc/wam';

// Finders
import { findCropList } from 'crops/finders';
import { findAllOpsAndFields } from 'operations/finders';
import {
  findEligibleProducts,
  findProductFeaturesByFieldUuid
} from 'product-features/finders';
import { findUserConfigFlags, findUserDetails, findSupportInfo, findProductValidationValues } from 'user/finders';

// Action Creators
import { addModalMessage, closeMessage } from '../messages/index';
import {
  completeAuthentication,
  completeCropListLoading,
  completeFieldsLoading,
  completeFlagsLoading,
  completeOperationLoading,
  completeUserId
} from 'api/loading';
import {
  setActiveUserId,
  setActiveFieldIdentifier,
  setActiveOperationUuid
} from 'current';
import {
  setAuthClient,
  setEnv,
  setHasDoneFirstLoad,
  setReferrer
} from 'settings';

// Selectors
import {
  getActiveFieldId,
  getActiveFieldUuid,
  getActiveUserId
} from 'current/selectors';
import { getAllFields } from 'fields/selectors';
import { getSupportInfo } from 'user/selectors';

// Utils
import { installNavBar } from 'util/navbar';
import {
  determineActiveField,
  determineEnv,
  determineUserId
} from 'util/env';
import { authenticate } from 'api/shared';
import { createFlagCheck } from 'user/feature-flags';
import { extractFieldIdentifier } from 'fields/utils';

import { requestFieldData } from 'init/requestFieldData';

// Constants
import { timeouts } from 'constants/values';
import { initializeHeap } from 'analytics/heap';
import { farmerNavigationStorageKeys } from 'constants/operations';
import { FIELD_VIEW_URL } from 'constants/urls';


// We have to load a lot of data before we can really get started. But it is
// kind of nice to have it in one clear(ish) list.
//
// TODO: Implement a priority queue so we can pile on lazy-loading without denying more urgent needs
// TODO: Convert more to lazy-loading (see finders for cropList for example)
//
// Basic principles:  (see docs/error-handling.md for details)
// * try not to return data through the chain - that makes inserting steps complicated
// * if you want to prevent an error from stopping later steps, make sure to put in a catch() asap after that then()
// * use treatErrorAs({}) with errorLeveL INFO or WARN and it will not interupt the flow
// * guarantee that any thenable called from here WILL return a thenable, even on error (see finders for examples)
// * to be very sure of catching and acting on errors, end every chain with a catch() with andHandleNow:true
// * AND end every chain with a then() calling handleErrorsInQueue()
// * otherwise an important error that was stopped or missed will never get acted on

const loadInitialPageData = (
  { dispatch, getState, displayNavBar = true, selectedOperation = undefined }
) => {
  let cropListPromise = Promise.resolve();

  // there is a risk this will be slow - if it's too slow, let's let the user know we care
  const tooLongTimer = setTimeout( () => {
    dispatch( addModalMessage({
      id: 'slow-load',
      blocking: false,
      bodyHtmlMessageKey: 'help-text__failure--load--slow',
      bodyHtmlMessageParams: getSupportInfo(getState())
    }) );
  }, timeouts.INITIAL_PAGE_LOAD_WARNING);

  // Authentication

  /* Note - until findUserDetails() resolves below, any displayed error messages will show default
     support info that may not correspond to user's country. */
  return authenticate()
  .then( () => {
    dispatch(completeAuthentication());
    dispatch(setReferrer( (document && document.referrer) || '' ));
    return Promise.resolve(findSupportInfo({ dispatch, getState }));
  })
  // Record that userId and general environment
  .then( () => {
    const userId = determineUserId();

    dispatch(setActiveUserId(userId));
    dispatch(completeUserId());

    dispatch( setAuthClient(wam.getAuthClientId()) );
    dispatch( setEnv(determineEnv()) );
    return Promise.all([
      findUserDetails({ dispatch, getState, userId }), // After this resolves, any error messages displayed should show support info specific to user.
      findUserConfigFlags({ dispatch, getState })
      .catch( treatErrorAs({ severity: errorLevel.WARN }) )
      .then( () => dispatch(completeFlagsLoading()) )
    ]);
  })
  .then((params) => {
    const [details] = params;
    return findProductValidationValues({
      dispatch,
      getState,
      accountCountry: details.accountCountry,
      unitsOfMeasureTemplate: details.unitsOfMeasureTemplate
    });
  })
  .then( () => {
    const state = getState();
    const userId = getActiveUserId(state);
    initializeHeap({ id: userId ,...state.user });
    
    // Enclose state for feature flag checks
    createFlagCheck({ getState });
    // Operations
    return findAllOpsAndFields({ dispatch, getState, forceWait: true, selectedOperation });
  })
  .then( ({ defaultOperationUuid }) => {
    // If a field is in the url, use that operation and that field
    // Else, if we have no operation, pick a field and use that operation
    // Else, if we have an operation, keep it and pick the first alphanumeric field of that op

    const fields = getAllFields(getState());
    // Once the scriptCreatorFieldsFromOl feature flag is removed, the operationUuid will be
    // defaultOperationUuid from the previous promise, where is defined reading the 
    // session storage or using the getDefaultOperationUuid function.
    const operationUuid = selectedOperation 
      || sessionStorage.getItem( farmerNavigationStorageKeys.OPERATION_SELECTED )
      || defaultOperationUuid;
    const identifier = determineActiveField({ fields, operationUuid });
    
    return new Promise((resolve, reject) => {
      if (!identifier) {
        reject('no active field');
        registerError({
          actions: [
            {
              textKey: 'button-text__create-fields',
              action: () => { window.location = FIELD_VIEW_URL.FIELD_SELECTION; }
            },
            {
              textKey: 'button-text__upload-fields',
              action: () => { window.location = FIELD_VIEW_URL.DATA_INBOX; }
            }
          ],
          messageKey: 'help-text__no-fields',
          severity: errorLevel.BLOCK,
          displayErrorAs: errorDisplayTypes.MODAL
        });
      }
      resolve(identifier);
    });
  })
  .then( (identifier) => {
    const operationUuid = identifier.operationUuid || '';
    dispatch(setActiveFieldIdentifier({ fieldId: identifier.id, fieldUuid: identifier.uuid }));
    dispatch(setActiveOperationUuid(operationUuid));
    dispatch(completeOperationLoading());
    dispatch(completeFieldsLoading());

    cropListPromise = findCropList({
      dispatch,
      getState,
      onError: treatErrorAs({ severity: errorLevel.BLOCK }),
      forceWait: true
    })
    .then( () => dispatch(completeCropListLoading()) );
  })
  // Fields
  .then( () => {
    const state = getState();
    const fields = getAllFields(state);
    const fieldId = getActiveFieldId(state);
    const fieldUuid = extractFieldIdentifier({ fields, id:fieldId }).uuid;

    if (fieldUuid) {
      requestFieldData({ dispatch, getState, fieldId, fieldUuid });
    }
  })
  .then( () => {
    if(displayNavBar) installNavBar({ dispatch, getState });
    const state = getState();
    const eligibleProducts = findEligibleProducts({ dispatch, getState });
        
    eligibleProducts.catch( treatErrorAs({ severity: errorLevel.WARN }) );

    const fieldUuid = getActiveFieldUuid(state);
    if (fieldUuid) {
      findProductFeaturesByFieldUuid(
        { dispatch, getState, fieldUuid: getActiveFieldUuid(state) }
      ).catch( treatErrorAs({ severity: errorLevel.WARN }) );
    }
  })
  .then( () => cropListPromise )

  // Last chance error-handling
  .catch( treatErrorAs({ severity: errorLevel.BLOCK, andStopHere: true, andHandleNow: true }) )
  .then( () => handleErrorsInQueue() )
  .then( () => {
    clearTimeout(tooLongTimer);
    dispatch(closeMessage('slow-load'));
    dispatch(setHasDoneFirstLoad(true));
  })
  .catch( treatErrorAs({message:'untrapped error', severity:errorLevel.INFO}) );
};

export default loadInitialPageData;
